import React, {useEffect, useState, useCallback} from 'react';
import {useLazyQuery, useEvent} from '@soap/modules';
import {SpaceTopAndBottom} from "../../../z40-uicomp/layout";
import {ChangeAccountNameDialog} from "../dialogs/ChangeAccountNameDialog";
import {useUserRole} from "../../../hooks/useUserRole";
import {CenterSpinner} from '@soap/baseweb';
import {Card, CardContent} from "../../ui/card";

const viewStates = {
    loading: 0,
    ready: 1,
};

export function AccountDetails() {
    const [getOrg, gotOrg] = useLazyQuery("C241v1_GetOrganisationDetails");
    const { requestingUserIsOrgAdmin, isOrgUser } = useUserRole();
    const [viewState, setViewState] = useState(viewStates.loading);
    const [orgData, setOrgData] = useState(gotOrg);
    
    // Only fetch org details if user has organization access
    const hasOrgAccess = isOrgUser || requestingUserIsOrgAdmin;
    
    useEffect(() => {
        if (hasOrgAccess) {
            getOrg({});
        } else {
            // User doesn't have org access, set to ready state without data
            setViewState(viewStates.ready);
        }
    }, [hasOrgAccess, getOrg]);

    // Update viewState and orgData when data is received
    useEffect(() => {
        if (gotOrg) {
            setOrgData(gotOrg);
            setViewState(viewStates.ready);
        }
    }, [gotOrg]);

    // Handle account name update without refetching
    const handleNameUpdated = useCallback((newName: string) => {
        setOrgData((prev) => {
            if (prev) {
                return {
                    ...prev,
                    e193_OrganisationName: newName
                };
            }
            return prev;
        });
    }, []);

    // Note: Name update is handled via callback from ChangeAccountNameDialog
    // No need to refetch on E194v1_OrganisationNameUpdated

    useEvent({
        eventName: "E197v1_OrganisationMembersUpdated",
        onEventReceived: () => {
            if (hasOrgAccess) {
                setViewState(viewStates.loading);
                getOrg({});
            }
        }
    }, [hasOrgAccess, getOrg]);

    // If user doesn't have org access, don't render the component
    if (!hasOrgAccess) {
        return null;
    }
    
    switch (viewState) {
        case viewStates.loading:
            return <CenterSpinner />;
        case viewStates.ready:
            return (
                <SpaceTopAndBottom>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                                {/* Account Name Section - Left */}
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-500 mb-2">Account Name</div>
                                    <div className="text-xl font-semibold text-gray-900 break-words">
                                        {orgData?.e193_OrganisationName}
                                    </div>
                                </div>
                                
                                {/* Stats Section - Middle */}
                                <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 lg:flex-1 lg:justify-center">
                                    {requestingUserIsOrgAdmin ? (
                                        <>
                                            <a 
                                                href="#/app/settings/users/members" 
                                                className="flex-1 sm:flex-none bg-action-blue/10 rounded-lg p-4 min-w-[140px] hover:bg-action-blue/15 transition-colors cursor-pointer"
                                            >
                                                <div className="text-2xl font-bold text-action-blue mb-1">
                                                    {orgData?.e193_ActiveSubAccounts}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    logins active
                                                </div>
                                            </a>
                                            <a 
                                                href="#/app/settings/users/invitations" 
                                                className="flex-1 sm:flex-none bg-gray-50 rounded-lg p-4 min-w-[140px] hover:bg-gray-100 transition-colors cursor-pointer"
                                            >
                                                <div className="text-2xl font-bold text-gray-700 mb-1">
                                                    {orgData?.e193_AvailableSubAccounts}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    logins available
                                                </div>
                                            </a>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-1 sm:flex-none bg-action-blue/10 rounded-lg p-4 min-w-[140px]">
                                                <div className="text-2xl font-bold text-action-blue mb-1">
                                                    {orgData?.e193_ActiveSubAccounts}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    logins active
                                                </div>
                                            </div>
                                            <div className="flex-1 sm:flex-none bg-gray-50 rounded-lg p-4 min-w-[140px]">
                                                <div className="text-2xl font-bold text-gray-700 mb-1">
                                                    {orgData?.e193_AvailableSubAccounts}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    logins available
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                
                                {/* Change Name Button - Right */}
                                {requestingUserIsOrgAdmin && (
                                    <div className="flex-shrink-0">
                                        <ChangeAccountNameDialog onNameUpdated={handleNameUpdated} />
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </SpaceTopAndBottom>
            );
        default:
            return null;
    }
}

